/**
 * MAD-1983 — read the GPU aggregator layout override from the URL.
 *
 * The viewer exposes a perf-investigation knob via the `?aggregator=`
 * query param so the SAME deployed bundle can be A/B-benchmarked in both
 * the Packed and Unpacked aggregator storage-buffer layouts on one
 * device, without a rebuild:
 *
 *   https://math.mad9000.studio/?aggregator=packed
 *   https://math.mad9000.studio/?aggregator=unpacked
 *   https://math.mad9000.studio/?aggregator=auto      (= the production default)
 *
 * The value is forwarded verbatim through the engine bridge to the Rust
 * `run_webgpu_metrics` aggregator path, which forces the layout
 * regardless of the adapter's `maxStorageBuffersPerShaderStage`. The
 * `[MAD-1972]` console log reports `FORCED AggregatorLayout=…` so the
 * operator can confirm the override took effect.
 *
 * Only `packed` / `unpacked` / `auto` are recognised (case-insensitive).
 * Anything else — including a hostile `?aggregator=💥` on the live site —
 * is treated as absent (production default) so the value, which is fully
 * user-controllable, can never break a run. The Rust side independently
 * re-validates and warns on garbage, so this is defence in depth.
 */

import type { AggregatorLayoutOverride } from "./engine/types";

const VALID = new Set<AggregatorLayoutOverride>(["packed", "unpacked", "auto"]);

/**
 * Parse an aggregator-layout override from a raw query-string value
 * (e.g. `URLSearchParams.get("aggregator")`). Returns `undefined` for a
 * `null`/empty/unrecognised value (the caller then omits the field, and
 * both the bridge and Rust treat absence as `auto`).
 */
export function parseAggregatorOverride(
  raw: string | null | undefined,
): AggregatorLayoutOverride | undefined {
  if (raw == null) {
    return undefined;
  }
  const normalized = raw.trim().toLowerCase();
  return VALID.has(normalized as AggregatorLayoutOverride)
    ? (normalized as AggregatorLayoutOverride)
    : undefined;
}

/**
 * Read the override from the current page URL's `?aggregator=` param.
 * Safe to call during module init / render — guards on `window` so it
 * is a no-op under SSR / test environments without a `location`.
 */
export function readAggregatorOverrideFromUrl(): AggregatorLayoutOverride | undefined {
  if (typeof window === "undefined" || typeof window.location === "undefined") {
    return undefined;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return parseAggregatorOverride(params.get("aggregator"));
  } catch {
    return undefined;
  }
}
